// src/utils/cacheService.ts
export interface CacheConfig {
    name: string;
    version: number;
    maxAge: number; // in milliseconds
    maxEntries: number;
  }
  
  export interface CachedResponse {
    data: any;
    timestamp: number;
    expires: number;
    etag?: string;
    lastModified?: string;
  }
  
  export interface CacheStats {
    name: string;
    size: number;
    entries: number;
    hitRate: number;
    lastAccess: number;
  }
  
  class CacheService {
    private cacheConfigs: Map<string, CacheConfig> = new Map();
    private hitCounts: Map<string, number> = new Map();
    private missCounts: Map<string, number> = new Map();
  
    constructor() {
      this.setupDefaultCaches();
    }
  
    // Setup default cache configurations
    private setupDefaultCaches(): void {
      this.cacheConfigs.set('api-responses', {
        name: 'staffluent-api-v1',
        version: 1,
        maxAge: 5 * 60 * 1000, // 5 minutes
        maxEntries: 100,
      });
  
      this.cacheConfigs.set('user-data', {
        name: 'staffluent-users-v1',
        version: 1,
        maxAge: 30 * 60 * 1000, // 30 minutes
        maxEntries: 50,
      });
  
      this.cacheConfigs.set('projects', {
        name: 'staffluent-projects-v1',
        version: 1,
        maxAge: 15 * 60 * 1000, // 15 minutes
        maxEntries: 200,
      });
  
      this.cacheConfigs.set('tasks', {
        name: 'staffluent-tasks-v1',
        version: 1,
        maxAge: 10 * 60 * 1000, // 10 minutes
        maxEntries: 500,
      });
  
      this.cacheConfigs.set('static-data', {
        name: 'staffluent-static-v1',
        version: 1,
        maxAge: 24 * 60 * 60 * 1000, // 24 hours
        maxEntries: 50,
      });
    }
  
    // Check if Cache API is supported
    isSupported(): boolean {
      return 'caches' in window;
    }
  
    // Get or create cache
    private async getCache(cacheType: string): Promise<Cache> {
      if (!this.isSupported()) {
        throw new Error('Cache API is not supported');
      }
  
      const config = this.cacheConfigs.get(cacheType);
      if (!config) {
        throw new Error(`Unknown cache type: ${cacheType}`);
      }
  
      return await caches.open(config.name);
    }
  
    // Cache a response
    async cacheResponse(
      cacheType: string,
      key: string,
      data: any,
      headers?: { etag?: string; lastModified?: string }
    ): Promise<void> {
      try {
        const cache = await this.getCache(cacheType);
        const config = this.cacheConfigs.get(cacheType)!;
        
        const cachedResponse: CachedResponse = {
          data,
          timestamp: Date.now(),
          expires: Date.now() + config.maxAge,
          etag: headers?.etag,
          lastModified: headers?.lastModified,
        };
  
        const response = new Response(JSON.stringify(cachedResponse), {
          headers: {
            'Content-Type': 'application/json',
            'Cache-Timestamp': cachedResponse.timestamp.toString(),
            'Cache-Expires': cachedResponse.expires.toString(),
          },
        });
  
        await cache.put(key, response);
        
        // Clean up old entries if needed
        await this.cleanupCache(cacheType);
        
      } catch (error) {
        console.error('Failed to cache response:', error);
      }
    }
  
    // Get cached response
    async getCachedResponse(cacheType: string, key: string): Promise<any | null> {
      try {
        const cache = await this.getCache(cacheType);
        const response = await cache.match(key);
  
        if (!response) {
          this.recordMiss(cacheType);
          return null;
        }
  
        const text = await response.text();
        const cachedResponse: CachedResponse = JSON.parse(text);
  
        // Check if expired
        if (Date.now() > cachedResponse.expires) {
          await cache.delete(key);
          this.recordMiss(cacheType);
          return null;
        }
  
        this.recordHit(cacheType);
        return cachedResponse.data;
        
      } catch (error) {
        console.error('Failed to get cached response:', error);
        this.recordMiss(cacheType);
        return null;
      }
    }
  
    // Cache API call with automatic caching
    async cacheApiCall<T>(
      cacheType: string,
      url: string,
      fetchFn: () => Promise<T>,
      forceRefresh: boolean = false
    ): Promise<T> {
      const cacheKey = this.generateCacheKey(url);
  
      // Try to get from cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cached = await this.getCachedResponse(cacheType, cacheKey);
        if (cached !== null) {
          return cached;
        }
      }
  
      // Fetch fresh data
      try {
        const data = await fetchFn();
        
        // Cache the response
        await this.cacheResponse(cacheType, cacheKey, data);
        
        return data;
      } catch (error) {
        // If fetch fails and we're online, return cached data if available
        if (navigator.onLine) {
          const cached = await this.getCachedResponse(cacheType, cacheKey);
          if (cached !== null) {
            console.warn('API call failed, returning cached data:', error);
            return cached;
          }
        }
        throw error;
      }
    }
  
    // Cache API call with conditional requests (ETags/Last-Modified)
    async cacheApiCallConditional<T>(
      cacheType: string,
      url: string,
      fetchFn: (headers?: Record<string, string>) => Promise<{ data: T; etag?: string; lastModified?: string }>,
      forceRefresh: boolean = false
    ): Promise<T> {
      const cacheKey = this.generateCacheKey(url);
  
      if (!forceRefresh) {
        // Try to get from cache
        const cache = await this.getCache(cacheType);
        const response = await cache.match(cacheKey);
  
        if (response) {
          const text = await response.text();
          const cachedResponse: CachedResponse = JSON.parse(text);
  
          // If not expired, return cached data
          if (Date.now() <= cachedResponse.expires) {
            this.recordHit(cacheType);
            return cachedResponse.data;
          }
  
          // If expired but we have etag/lastModified, try conditional request
          const conditionalHeaders: Record<string, string> = {};
          if (cachedResponse.etag) {
            conditionalHeaders['If-None-Match'] = cachedResponse.etag;
          }
          if (cachedResponse.lastModified) {
            conditionalHeaders['If-Modified-Since'] = cachedResponse.lastModified;
          }
  
          try {
            const result = await fetchFn(conditionalHeaders);
            
            // Update cache with fresh data
            await this.cacheResponse(cacheType, cacheKey, result.data, {
              etag: result.etag,
              lastModified: result.lastModified,
            });
            
            return result.data;
            
          } catch (error) {
            // If conditional request fails with 304, data hasn't changed
            if ((error as any).status === 304) {
              // Update expiry time and return cached data
              cachedResponse.expires = Date.now() + this.cacheConfigs.get(cacheType)!.maxAge;
              await this.cacheResponse(cacheType, cacheKey, cachedResponse.data, {
                etag: cachedResponse.etag,
                lastModified: cachedResponse.lastModified,
              });
              this.recordHit(cacheType);
              return cachedResponse.data;
            }
            
            // For other errors, fall back to cached data if available
            this.recordMiss(cacheType);
            return cachedResponse.data;
          }
        }
      }
  
      // No cache or forced refresh - fetch fresh data
      try {
        const result = await fetchFn();
        await this.cacheResponse(cacheType, cacheKey, result.data, {
          etag: result.etag,
          lastModified: result.lastModified,
        });
        return result.data;
      } catch (error) {
        this.recordMiss(cacheType);
        throw error;
      }
    }
  
    // Generate cache key from URL
    private generateCacheKey(url: string): string {
      // Remove query parameters that don't affect caching
      const urlObj = new URL(url, window.location.origin);
      const params = new URLSearchParams(urlObj.search);
      
      // Remove timestamp and cache-busting parameters
      params.delete('_t');
      params.delete('cache');
      params.delete('timestamp');
      
      urlObj.search = params.toString();
      return urlObj.toString();
    }
  
    // Clean up expired entries and enforce size limits
    private async cleanupCache(cacheType: string): Promise<void> {
      try {
        const cache = await this.getCache(cacheType);
        const config = this.cacheConfigs.get(cacheType)!;
        
        const keys = await cache.keys();
        
        // Get all entries with their timestamps
        const entries: { key: Request; timestamp: number; expires: number }[] = [];
        
        for (const key of keys) {
          const response = await cache.match(key);
          if (response) {
            try {
              const text = await response.text();
              const cachedResponse: CachedResponse = JSON.parse(text);
              entries.push({
                key,
                timestamp: cachedResponse.timestamp,
                expires: cachedResponse.expires,
              });
            } catch (error) {
              // Invalid entry, add to deletion list
              await cache.delete(key);
            }
          }
        }
  
        // Remove expired entries
        const now = Date.now();
        const expiredEntries = entries.filter(entry => now > entry.expires);
        for (const entry of expiredEntries) {
          await cache.delete(entry.key);
        }
  
        // Remove oldest entries if over limit
        const validEntries = entries.filter(entry => now <= entry.expires);
        if (validEntries.length > config.maxEntries) {
          validEntries.sort((a, b) => a.timestamp - b.timestamp);
          const entriesToRemove = validEntries.slice(0, validEntries.length - config.maxEntries);
          
          for (const entry of entriesToRemove) {
            await cache.delete(entry.key);
          }
        }
      } catch (error) {
        console.error('Cache cleanup failed:', error);
      }
    }
  
    // Clear specific cache
    async clearCache(cacheType: string): Promise<void> {
      try {
        const config = this.cacheConfigs.get(cacheType);
        if (config) {
          await caches.delete(config.name);
        }
      } catch (error) {
        console.error('Failed to clear cache:', error);
      }
    }
  
    // Clear all caches
    async clearAllCaches(): Promise<void> {
      try {
        for (const config of this.cacheConfigs.values()) {
          await caches.delete(config.name);
        }
        this.hitCounts.clear();
        this.missCounts.clear();
      } catch (error) {
        console.error('Failed to clear all caches:', error);
      }
    }
  
    // Get cache statistics
    async getCacheStats(): Promise<CacheStats[]> {
      const stats: CacheStats[] = [];
  
      for (const [type, config] of this.cacheConfigs.entries()) {
        try {
          const cache = await this.getCache(type);
          const keys = await cache.keys();
          
          let totalSize = 0;
          let lastAccess = 0;
  
          for (const key of keys) {
            const response = await cache.match(key);
            if (response) {
              const text = await response.text();
              totalSize += text.length;
              
              try {
                const cachedResponse: CachedResponse = JSON.parse(text);
                lastAccess = Math.max(lastAccess, cachedResponse.timestamp);
              } catch (error) {
                // Invalid entry
              }
            }
          }
  
          const hits = this.hitCounts.get(type) || 0;
          const misses = this.missCounts.get(type) || 0;
          const hitRate = hits + misses > 0 ? hits / (hits + misses) : 0;
  
          stats.push({
            name: config.name,
            size: totalSize,
            entries: keys.length,
            hitRate,
            lastAccess,
          });
        } catch (error) {
          console.error(`Failed to get stats for cache ${type}:`, error);
        }
      }
  
      return stats;
    }
  
    // Record cache hit
    private recordHit(cacheType: string): void {
      const current = this.hitCounts.get(cacheType) || 0;
      this.hitCounts.set(cacheType, current + 1);
    }
  
    // Record cache miss
    private recordMiss(cacheType: string): void {
      const current = this.missCounts.get(cacheType) || 0;
      this.missCounts.set(cacheType, current + 1);
    }
  
    // Preload critical data
    async preloadCriticalData(apiCalls: Array<{
      type: string;
      url: string;
      fetchFn: () => Promise<any>;
    }>): Promise<void> {
      
      const promises = apiCalls.map(async ({ type, url, fetchFn }) => {
        try {
          await this.cacheApiCall(type, url, fetchFn);
          // @ts-ignore
        } catch (error) {
        }
      });
  
      await Promise.allSettled(promises);
    }
  
    // Storage usage estimation
    async getStorageUsage(): Promise<{ used: number; quota: number; percentage: number }> {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        try {
          const estimate = await navigator.storage.estimate();
          const used = estimate.usage || 0;
          const quota = estimate.quota || 0;
          const percentage = quota > 0 ? (used / quota) * 100 : 0;
  
          return { used, quota, percentage };
        } catch (error) {
          console.error('Failed to get storage estimate:', error);
        }
      }
  
      return { used: 0, quota: 0, percentage: 0 };
    }
  }
  
  // Export singleton instance
  export const cacheService = new CacheService();